/**
 * Tiny safe markdown renderer for legal/changelog pages.
 *
 * Supports the subset used by `terms.md` + `changelog.md`:
 *   - `# H1`, `## H2`, `### H3`
 *   - `**bold**`
 *   - `- bullet` (unordered list)
 *   - blank-line paragraph separation
 *
 * No HTML pass-through, no `dangerouslySetInnerHTML` — every node is a real
 * React element so user-supplied text cannot inject markup.
 */

import React from 'react';

const stripHtmlComments = (md: string): string =>
  md.replace(/<!--[\s\S]*?-->/g, '');

const renderInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  const pattern = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(<strong key={`${keyPrefix}-b-${i++}`}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

interface Block {
  type: 'h1' | 'h2' | 'h3' | 'ul' | 'p';
  text?: string;
  items?: string[];
}

const parse = (md: string): Block[] => {
  const lines = stripHtmlComments(md).split(/\r?\n/);
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let listItems: string[] | null = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: 'p', text: paragraph.join(' ') });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (listItems) {
      blocks.push({ type: 'ul', items: listItems });
      listItems = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line === '') {
      flushParagraph();
      flushList();
      continue;
    }
    if (line.startsWith('### ')) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'h3', text: line.slice(4) });
    } else if (line.startsWith('## ')) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'h2', text: line.slice(3) });
    } else if (line.startsWith('# ')) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'h1', text: line.slice(2) });
    } else if (line.startsWith('- ')) {
      flushParagraph();
      listItems = listItems ?? [];
      listItems.push(line.slice(2));
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushList();
  return blocks;
};

export interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className }) => {
  const blocks = parse(content);
  return (
    <div className={className}>
      {blocks.map((b, idx) => {
        const key = `block-${idx}`;
        switch (b.type) {
          case 'h1':
            return (
              <h1 key={key} className="text-2xl font-bold mt-6 mb-3">
                {renderInline(b.text!, key)}
              </h1>
            );
          case 'h2':
            return (
              <h2 key={key} className="text-xl font-semibold mt-5 mb-2">
                {renderInline(b.text!, key)}
              </h2>
            );
          case 'h3':
            return (
              <h3 key={key} className="text-lg font-semibold mt-4 mb-2">
                {renderInline(b.text!, key)}
              </h3>
            );
          case 'ul':
            return (
              <ul key={key} className="list-disc list-inside mb-3 space-y-1">
                {b.items!.map((item, i) => (
                  <li key={`${key}-li-${i}`}>{renderInline(item, `${key}-li-${i}`)}</li>
                ))}
              </ul>
            );
          case 'p':
          default:
            return (
              <p key={key} className="mb-3 leading-relaxed">
                {renderInline(b.text!, key)}
              </p>
            );
        }
      })}
    </div>
  );
};

MarkdownRenderer.displayName = 'MarkdownRenderer';

export default MarkdownRenderer;
